import { z } from 'zod';
/**
 * The field-type catalogue.
 *
 * A CRM record is a bag of fields, and every field has exactly one type from this closed
 * set. The set is closed ON PURPOSE: a type here means the API knows how to validate and
 * store it, the web app knows how to render and edit it, and filters know which operators
 * apply. Adding a type is a change to all three, so it is a change to this file first.
 */
export const FIELD_TYPES = [
    'text',
    'long_text',
    'number',
    'currency',
    'boolean',
    'date',
    'datetime',
    'email',
    'phone',
    'url',
    'single_select',
    'multi_select',
    'user',
    'reference',
    'json',
];
export const fieldTypeSchema = z.enum(FIELD_TYPES);
/** Filter operators a field type supports. Kept as a closed set so a query builder cannot
 *  send an operator the storage layer has no translation for. */
export const FILTER_OPERATORS = [
    'eq',
    'neq',
    'contains',
    'starts_with',
    'gt',
    'gte',
    'lt',
    'lte',
    'in',
    'not_in',
    'is_empty',
    'is_not_empty',
];
export const filterOperatorSchema = z.enum(FILTER_OPERATORS);
/** The typed storage column a value of this type lives in (`custom_field_values`, RELAY-941). */
export const SQL_COLUMNS = [
    'value_text',
    'value_number',
    'value_date',
    'value_bool',
    'value_ref',
    'value_json',
];
const blank = (raw) => raw.trim() === '';
const parseNumber = (raw) => {
    if (blank(raw))
        return undefined;
    const n = Number(raw.replace(/,/g, ''));
    if (!Number.isFinite(n))
        throw new Error(`not a number: ${raw}`);
    return n;
};
const parseBoolean = (raw) => {
    if (blank(raw))
        return undefined;
    const v = raw.trim().toLowerCase();
    if (['true', 'yes', 'y', '1', 'x'].includes(v))
        return true;
    if (['false', 'no', 'n', '0'].includes(v))
        return false;
    throw new Error(`not a yes/no: ${raw}`);
};
const parseDate = (raw) => {
    if (blank(raw))
        return undefined;
    const d = new Date(raw.trim());
    if (Number.isNaN(d.getTime()))
        throw new Error(`not a date: ${raw}`);
    return d.toISOString().slice(0, 10);
};
const parseDatetime = (raw) => {
    if (blank(raw))
        return undefined;
    const d = new Date(raw.trim());
    if (Number.isNaN(d.getTime()))
        throw new Error(`not a date-time: ${raw}`);
    return d.toISOString();
};
const parseList = (raw) => blank(raw)
    ? undefined
    : raw
        .split(/[;|,]/)
        .map((s) => s.trim())
        .filter(Boolean);
const parseJson = (raw) => (blank(raw) ? undefined : JSON.parse(raw));
const str = (v) => {
    if (v === null || v === undefined)
        return '';
    if (typeof v === 'string')
        return v;
    if (typeof v === 'number' || typeof v === 'boolean' || typeof v === 'bigint')
        return String(v);
    return JSON.stringify(v);
};
const textOps = [
    'eq',
    'neq',
    'contains',
    'starts_with',
    'in',
    'not_in',
    'is_empty',
    'is_not_empty',
];
const orderedOps = ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'is_empty', 'is_not_empty'];
const setOps = ['in', 'not_in', 'is_empty', 'is_not_empty'];
export const FIELD_TYPE_CATALOGUE = {
    text: {
        label: 'Text',
        hasOptions: false,
        hasTarget: false,
        operators: textOps,
        value: z.string(),
        sort: 'text',
        cellRenderer: 'text',
        editor: 'text',
        importParser: (raw) => (blank(raw) ? undefined : raw.trim()),
        csvFormatter: str,
        openApiSchema: { type: 'string' },
        sqlColumn: 'value_text',
    },
    long_text: {
        label: 'Long text',
        hasOptions: false,
        hasTarget: false,
        operators: textOps,
        value: z.string(),
        sort: 'text',
        cellRenderer: 'long_text',
        editor: 'textarea',
        importParser: (raw) => (blank(raw) ? undefined : raw.trim()),
        csvFormatter: str,
        openApiSchema: { type: 'string' },
        sqlColumn: 'value_text',
    },
    number: {
        label: 'Number',
        hasOptions: false,
        hasTarget: false,
        operators: orderedOps,
        // What `value_number numeric(20,6)` holds: refused here, never a server error or a value
        // rounded in silence.
        value: z
            .number()
            .finite()
            .refine((v) => Math.abs(v) < 1e14, 'must have at most 14 digits before the decimal point')
            .refine((v) => Number(v.toFixed(6)) === v, 'must have at most 6 decimal places'),
        sort: 'number',
        cellRenderer: 'number',
        editor: 'number',
        importParser: parseNumber,
        csvFormatter: str,
        openApiSchema: { type: 'number' },
        sqlColumn: 'value_number',
    },
    currency: {
        label: 'Currency',
        hasOptions: false,
        hasTarget: false,
        operators: orderedOps,
        // Minor units + ISO-4217 code. Never a float: 19.99 is not representable in binary.
        value: z.object({
            amountMinor: z.number().int(),
            currency: z.string().length(3).toUpperCase(),
        }),
        sort: 'number',
        cellRenderer: 'currency',
        editor: 'currency',
        // "12.50 USD" or "12.50" (USD assumed) ↔ { amountMinor: 1250, currency: 'USD' }
        importParser: (raw) => {
            if (blank(raw))
                return undefined;
            const m = /^\s*(-?\d+(?:\.\d{1,2})?)\s*([A-Za-z]{3})?\s*$/.exec(raw);
            if (!m)
                throw new Error(`not an amount: ${raw}`);
            return {
                amountMinor: Math.round(Number(m[1]) * 100),
                currency: (m[2] ?? 'USD').toUpperCase(),
            };
        },
        csvFormatter: (v) => {
            const c = v;
            return c && typeof c.amountMinor === 'number'
                ? `${(c.amountMinor / 100).toFixed(2)} ${c.currency ?? 'USD'}`
                : '';
        },
        openApiSchema: {
            type: 'object',
            properties: {
                amountMinor: { type: 'integer' },
                currency: { type: 'string', minLength: 3, maxLength: 3 },
            },
            required: ['amountMinor', 'currency'],
        },
        sqlColumn: 'value_json',
    },
    boolean: {
        label: 'Checkbox',
        hasOptions: false,
        hasTarget: false,
        operators: ['eq', 'neq'],
        value: z.boolean(),
        sort: 'boolean',
        cellRenderer: 'boolean',
        editor: 'checkbox',
        importParser: parseBoolean,
        csvFormatter: (v) => (v === true ? 'yes' : v === false ? 'no' : ''),
        openApiSchema: { type: 'boolean' },
        sqlColumn: 'value_bool',
    },
    date: {
        label: 'Date',
        hasOptions: false,
        hasTarget: false,
        operators: orderedOps,
        value: z.iso.date(),
        sort: 'date',
        cellRenderer: 'date',
        editor: 'date',
        importParser: parseDate,
        csvFormatter: str,
        openApiSchema: { type: 'string', format: 'date' },
        sqlColumn: 'value_date',
    },
    datetime: {
        label: 'Date & time',
        hasOptions: false,
        hasTarget: false,
        operators: orderedOps,
        value: z.iso.datetime({ offset: true }),
        sort: 'date',
        cellRenderer: 'datetime',
        editor: 'datetime',
        importParser: parseDatetime,
        csvFormatter: str,
        openApiSchema: { type: 'string', format: 'date-time' },
        sqlColumn: 'value_date',
    },
    email: {
        label: 'Email',
        hasOptions: false,
        hasTarget: false,
        operators: textOps,
        value: z.email(),
        sort: 'text',
        cellRenderer: 'email',
        editor: 'text',
        importParser: (raw) => (blank(raw) ? undefined : raw.trim().toLowerCase()),
        csvFormatter: str,
        openApiSchema: { type: 'string', format: 'email' },
        sqlColumn: 'value_text',
    },
    phone: {
        label: 'Phone',
        hasOptions: false,
        hasTarget: false,
        operators: textOps,
        // E.164 — the only format that round-trips through WhatsApp/SMS providers unchanged.
        value: z.string().regex(/^\+[1-9]\d{6,14}$/, 'Phone must be E.164 (+15551234567)'),
        sort: 'text',
        cellRenderer: 'phone',
        editor: 'text',
        importParser: (raw) => (blank(raw) ? undefined : raw.replace(/[\s().-]/g, '')),
        csvFormatter: str,
        openApiSchema: { type: 'string', pattern: '^\\+[1-9]\\d{6,14}$' },
        sqlColumn: 'value_text',
    },
    url: {
        label: 'URL',
        hasOptions: false,
        hasTarget: false,
        operators: textOps,
        value: z.url(),
        sort: 'text',
        cellRenderer: 'link',
        editor: 'text',
        importParser: (raw) => (blank(raw) ? undefined : raw.trim()),
        csvFormatter: str,
        openApiSchema: { type: 'string', format: 'uri' },
        sqlColumn: 'value_text',
    },
    single_select: {
        label: 'Single select',
        hasOptions: true,
        hasTarget: false,
        operators: ['eq', 'neq', ...setOps],
        value: z.string().min(1),
        sort: 'text',
        cellRenderer: 'badge',
        editor: 'select',
        importParser: (raw) => (blank(raw) ? undefined : raw.trim()),
        csvFormatter: str,
        openApiSchema: { type: 'string' },
        sqlColumn: 'value_text',
    },
    multi_select: {
        label: 'Multi select',
        hasOptions: true,
        hasTarget: false,
        operators: setOps,
        value: z.array(z.string().min(1)),
        sort: 'none',
        cellRenderer: 'badges',
        editor: 'multiselect',
        importParser: parseList,
        csvFormatter: (v) => (Array.isArray(v) ? v.map(String).join('; ') : ''),
        openApiSchema: { type: 'array', items: { type: 'string' } },
        sqlColumn: 'value_json',
    },
    user: {
        label: 'User',
        hasOptions: false,
        hasTarget: false,
        operators: ['eq', 'neq', ...setOps],
        value: z.uuid(),
        sort: 'none',
        cellRenderer: 'user',
        editor: 'user',
        importParser: (raw) => (blank(raw) ? undefined : raw.trim()),
        csvFormatter: str,
        openApiSchema: { type: 'string', format: 'uuid' },
        sqlColumn: 'value_ref',
    },
    reference: {
        label: 'Reference',
        hasOptions: false,
        hasTarget: true,
        operators: ['eq', 'neq', ...setOps],
        value: z.uuid(),
        sort: 'none',
        cellRenderer: 'reference',
        editor: 'reference',
        importParser: (raw) => (blank(raw) ? undefined : raw.trim()),
        csvFormatter: str,
        openApiSchema: { type: 'string', format: 'uuid' },
        sqlColumn: 'value_ref',
    },
    json: {
        label: 'JSON',
        hasOptions: false,
        hasTarget: false,
        operators: ['is_empty', 'is_not_empty'],
        value: z.unknown(),
        sort: 'none',
        cellRenderer: 'json',
        editor: 'json',
        importParser: parseJson,
        csvFormatter: (v) => (v === null || v === undefined ? '' : JSON.stringify(v)),
        openApiSchema: {},
        sqlColumn: 'value_json',
    },
};
/** A machine key: snake_case, starts with a letter, ≤ 64 chars. Shown in exports and the
 *  API, so it is deliberately not free text. */
export const fieldKeySchema = z
    .string()
    .regex(/^[a-z][a-z0-9_]{0,63}$/, 'Field key must be snake_case, start with a letter, ≤ 64 chars');
export const selectOptionSchema = z.object({
    value: z.string().min(1).max(100),
    label: z.string().min(1).max(200),
    color: z
        .string()
        .regex(/^#[0-9a-fA-F]{6}$/)
        .optional(),
});
/**
 * Entity kinds that can carry custom fields today (companies and projects since TS-1751,
 * contacts, jobs and deals since TS-1840). Extending this is a reviewed change: every read and
 * write path of the entity must load its definitions through `definitionsFor`, or a hidden
 * field leaks.
 */
export const CUSTOM_FIELD_ENTITIES = [
    'candidates',
    'companies',
    'contacts',
    'projects',
    'deals',
    'jobs',
];
export const customFieldEntitySchema = z.enum(CUSTOM_FIELD_ENTITIES);
/** Per-role visibility of a field: hidden from the role, read-only, or editable. */
export const FIELD_VISIBILITY = ['hidden', 'read', 'edit'];
export const fieldVisibilitySchema = z.enum(FIELD_VISIBILITY);
/**
 * What one role may do with one field (TS-956). The definition's `visibility` names the
 * exceptions; a role it does not list may edit. A member who can manage field definitions
 * always gets `edit`, so an admin can never hide a field from the people who configure it.
 */
export function fieldAccess(def, roleKey, managesFields) {
    if (managesFields)
        return 'edit';
    return def.visibility?.[roleKey] ?? 'edit';
}
/** The definitions a role may see at all: everything but its `hidden` ones. */
export function visibleDefinitions(defs, roleKey, managesFields) {
    return defs.filter((d) => fieldAccess(d, roleKey, managesFields) !== 'hidden');
}
/**
 * Drop the `custom.<key>` entries of hidden fields from an audit `changes` object, so a
 * hidden value cannot be read back through a record's history or the audit log.
 */
export function redactHiddenChanges(changes, hiddenKeys) {
    if (!changes || hiddenKeys.size === 0)
        return changes;
    const out = {};
    for (const [k, v] of Object.entries(changes)) {
        if (k.startsWith('custom.') && hiddenKeys.has(k.slice('custom.'.length)))
            continue;
        out[k] = v;
    }
    return out;
}
/**
 * How a field's value reads in a list cell and in a filter (e.g. `$` before an hourly rate):
 * text around the value and, for numbers, a fixed number of decimals.
 */
/** The colours a value can be shown in: good (green), caution (yellow), bad (red). */
export const VALUE_TONES = ['positive', 'caution', 'negative'];
/**
 * A colour band of a number: values up to `upTo` (inclusive) take its tone; bands are read in
 * order and the last one has no `upTo` (everything above). E.g. an hourly rate:
 * `[{ upTo: 10, tone: 'positive' }, { upTo: 15, tone: 'caution' }, { tone: 'negative' }]`.
 */
export const valueBandSchema = z
    .object({ upTo: z.number().optional(), tone: z.enum(VALUE_TONES) })
    .strict();
export const fieldFormatSchema = z
    .object({
    prefix: z.string().max(8).optional(),
    suffix: z.string().max(8).optional(),
    decimals: z.number().int().min(0).max(6).optional(),
    bands: z
        .array(valueBandSchema)
        .min(1)
        .max(6)
        .refine((bands) => bands.every((b, i) => i === bands.length - 1
        ? b.upTo === undefined
        : b.upTo !== undefined && (i === 0 || b.upTo > bands[i - 1].upTo)), 'Bands go up in order, and only the last one has no upper limit')
        .optional(),
})
    .strict();
/** The tone of a number under the format's bands, or undefined (no bands, not a number). */
export function toneOf(value, format) {
    if (typeof value !== 'number' || !Number.isFinite(value) || !format?.bands)
        return undefined;
    return format.bands.find((b) => b.upTo === undefined || value <= b.upTo)?.tone;
}
/** A value as its format shows it; numbers are grouped (en-US) and rounded to `decimals`. */
export function formatFieldValue(value, format) {
    // NaN or Infinity is not a value to dress up as "$NaN".
    if (typeof value === 'number' && !Number.isFinite(value))
        return '';
    const text = typeof value === 'number'
        ? value.toLocaleString('en-US', {
            ...(format?.decimals === undefined
                ? { maximumFractionDigits: 20 }
                : { minimumFractionDigits: format.decimals, maximumFractionDigits: format.decimals }),
        })
        : String(value);
    return `${format?.prefix ?? ''}${text}${format?.suffix ?? ''}`;
}
/** A field definition as stored per entity kind and edited in Settings → Fields. */
export const fieldDefinitionBaseSchema = z.object({
    entity: customFieldEntitySchema,
    key: fieldKeySchema,
    label: z.string().min(1).max(200),
    type: fieldTypeSchema,
    required: z.boolean().default(false),
    options: z.array(selectOptionSchema).optional(),
    /** For `reference`: the entity kind the value points at. */
    target: z.string().min(1).optional(),
    description: z.string().max(1_000).optional(),
    /** Applied on create when the client sends no value. */
    defaultValue: z.unknown().optional(),
    /** Section heading the field sits under in forms. */
    group: z.string().max(100).optional(),
    /** Order inside its group. */
    position: z.number().int().min(0).default(0),
    /** Role key → visibility. A role not listed sees the field as `edit`. */
    visibility: z.record(z.string(), fieldVisibilitySchema).default({}),
    /** Exposed in filters and sorts (the storage columns are always indexed; this is the UI contract). */
    isIndexed: z.boolean().default(false),
    /** How list cells and filters show the value; `decimals` only applies to number and currency. */
    format: fieldFormatSchema.nullable().optional(),
});
function refineDefinition(def, ctx) {
    {
        const spec = FIELD_TYPE_CATALOGUE[def.type];
        if (spec.hasOptions && (!def.options || def.options.length === 0)) {
            ctx.addIssue({
                code: 'custom',
                path: ['options'],
                message: `${def.type} needs at least one option`,
            });
        }
        if (!spec.hasOptions && def.options) {
            ctx.addIssue({
                code: 'custom',
                path: ['options'],
                message: `${def.type} does not take options`,
            });
        }
        if (spec.hasTarget && !def.target) {
            ctx.addIssue({
                code: 'custom',
                path: ['target'],
                message: `${def.type} needs a target entity kind`,
            });
        }
        if (!spec.hasTarget && def.target) {
            ctx.addIssue({
                code: 'custom',
                path: ['target'],
                message: `${def.type} does not take a target`,
            });
        }
        if (def.defaultValue !== undefined && def.defaultValue !== null) {
            const parsed = FIELD_TYPE_CATALOGUE[def.type].value.safeParse(def.defaultValue);
            if (!parsed.success)
                ctx.addIssue({
                    code: 'custom',
                    path: ['defaultValue'],
                    message: `default is not a valid ${def.type}`,
                });
        }
    }
}
export const fieldDefinitionSchema = fieldDefinitionBaseSchema.superRefine(refineDefinition);
/** A definition as the API returns it. */
export const storedFieldDefinitionSchema = fieldDefinitionBaseSchema
    .extend({
    id: z.uuid(),
    createdAt: z.iso.datetime({ offset: true }),
    updatedAt: z.iso.datetime({ offset: true }),
})
    .superRefine(refineDefinition);
/** What Settings → Fields may change after creation (never entity, key or type: values are typed by them). */
export const fieldDefinitionPatchSchema = fieldDefinitionBaseSchema
    .omit({ entity: true, key: true, type: true })
    .partial();
/** The `custom` object on a record: definition key → value. */
export const customValuesSchema = z.record(fieldKeySchema, z.unknown());
/**
 * Validate a record's custom values against the ACTIVE definitions for its entity — what the
 * API runs on every write. `partial` (a PATCH) skips required-field checks for keys that are
 * absent; a create fills `defaultValue` for absent keys. Unknown keys are refused: a value
 * with no definition has no type, so it could not be stored or filtered.
 */
export function validateCustomValues(values, definitions, opts = {}) {
    const issues = [];
    const parsed = {};
    const byKey = new Map(definitions.map((d) => [d.key, d]));
    for (const [key, raw] of Object.entries(values ?? {})) {
        const def = byKey.get(key);
        if (!def) {
            issues.push({ key, message: `no custom field '${key}'` });
            continue;
        }
        const r = parseFieldValue(def, raw);
        if (r.success)
            parsed[key] = r.value;
        else
            issues.push({ key, message: r.error });
    }
    if (!opts.partial) {
        for (const def of definitions) {
            if (key_in(parsed, def.key))
                continue;
            if (def.defaultValue !== undefined && def.defaultValue !== null)
                parsed[def.key] = def.defaultValue;
            else if (def.required)
                issues.push({ key: def.key, message: `${def.key} is required` });
        }
    }
    return { issues, parsed };
}
const key_in = (o, k) => Object.prototype.hasOwnProperty.call(o, k);
/** The filter-field catalogue entries for an entity's custom fields: `custom.<key>` → type. */
export function customFilterFields(definitions) {
    const out = {};
    for (const d of definitions)
        if (d.isIndexed)
            out[`custom.${d.key}`] = d.type;
    return out;
}
/** Validate a VALUE against its definition — what the API runs on every write. */
export function parseFieldValue(def, value) {
    const spec = FIELD_TYPE_CATALOGUE[def.type];
    if (value === null || value === undefined) {
        return def.required
            ? { success: false, error: `${def.key} is required` }
            : { success: true, value: null };
    }
    const parsed = spec.value.safeParse(value);
    if (!parsed.success) {
        return { success: false, error: parsed.error.issues.map((i) => i.message).join('; ') };
    }
    if (spec.hasOptions && def.options) {
        const allowed = new Set(def.options.map((o) => o.value));
        const chosen = Array.isArray(parsed.data) ? parsed.data : [parsed.data];
        const bad = chosen.filter((c) => !allowed.has(String(c)));
        if (bad.length > 0) {
            return {
                success: false,
                error: `${def.key}: unknown option(s) ${bad.map(String).join(', ')}`,
            };
        }
    }
    return { success: true, value: parsed.data };
}
/** Does this operator apply to this field type? Used by the filter builder and the API. */
export function operatorAppliesTo(type, operator) {
    return FIELD_TYPE_CATALOGUE[type].operators.includes(operator);
}
//# sourceMappingURL=fields.js.map